import format from 'date-fns/format'
import it from 'date-fns/locale/it'
import dateFormat from 'dateformat'
import compact from 'lodash/compact'
import flatten from 'lodash/flatten'
import flattenDeep from 'lodash/flattenDeep'
import isArray from 'lodash/isArray'
import uniq from 'lodash/uniq'
import uniqBy from 'lodash/uniqBy'
import moment from 'moment-timezone'

export const APP_VET_MODE = process.env.REACT_APP_TYPE === 'web'

export const safeArray = (data: any): any[] => (isArray(data) ? data : [])
export const unique = (data: any[]): string[] => uniq(compact(flattenDeep(data))) || []
export const uniqueById = (data: any[], iteratee: string): any[] => uniqBy(flatten(data), iteratee)

export const arrayToDictionary = (data: any[], key: string): { [key: string]: any } =>
  data.reduce((acc: any, curr) => {
    acc[curr[key]] = curr
    return acc
  }, {})

export const DATE_MONTHS = [
  'Gennaio',
  'Febbraio',
  'Marzo',
  'Aprile',
  'Maggio',
  'Giugno',
  'Luglio',
  'Agosto',
  'Settembre',
  'Ottobre',
  'Novembre',
  'Dicembre',
]

export const DATE_MONTHS_US = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

// export const DATE_WEEKDAYS = ['Domenica', 'Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato']
export const DATE_WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

dateFormat.i18n = {
  dayNames: ['Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Domenica', 'Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato'],
  monthNames: [
    'Gen',
    'Feb',
    'Mar',
    'Apr',
    'Mag',
    'Giu',
    'Lug',
    'Ago',
    'Set',
    'Ott',
    'Nov',
    'Dic',
    'Gennaio',
    'Febbraio',
    'Marzo',
    'Aprile',
    'Maggio',
    'Giugno',
    'Luglio',
    'Agosto',
    'Settembre',
    'Ottobre',
    'Novembre',
    'Dicembre',
  ],
  timeNames: ['a', 'p', 'am', 'pm', 'A', 'P', 'AM', 'PM'],
}

export const formatDate = (date: any, f: string) => {
  if (!isValidDate(date)) {
    return ''
  }

  date.set

  return format(date, f, { locale: it })
}
const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
})

export const formatCurrency = (data: any): string => currencyFormatter.format(data)

export const isValidDate = (d: any): boolean => d instanceof Date && !isNaN(d.getTime())

export const fromDate = (date: any): Date => {
  const offsetTimezone = new Date().getTimezoneOffset() * 60000
  const dateTime = date.getTime()

  return new Date(dateTime - offsetTimezone)
}

export const getNow = () => {
  const offsetTimezone = new Date().getTimezoneOffset() * 60000
  const dateTime = new Date().getTime()

  return new Date(dateTime - offsetTimezone).toISOString()
}

export const DATE_FORMAT = 'dd/MM/yyyy'
export const TIME_FORMAT = 'HH:mm'
export const DATETIME_FORMAT = 'dd/MM/yyyy HH:mm'
export const LONG_DATE_FORMAT = 'EEEE, dd MMMM yyyy'

// const getTimezoneOffset = () => new Date().getTimezoneOffset() * 60000
const convertAnyToDate = (data: any): Date => (data instanceof Date ? data : new Date(data || ''))

export const DateConverter = {
  from(date: any): Date {
    const d = convertAnyToDate(date)
    return new Date(d.getTime())
  },

  fromServer(date: any): Date {
    const d = convertAnyToDate(date)
    return new Date(d.getTime())
  },
  /**
   * Generate a ISO date for the server that Ignore the offset
   * @param date
   */
  toServer(date: any): string | null {
    const d = convertAnyToDate(date)

    if (!isValidDate(d)) {
      return null
    }

    return new Date(d.getTime()).toISOString()
  },
  now() {
    // return new Date(new Date().getTime())
    return moment().utc().toDate()
  },
}

export const convertMillisecondsToUnits = (duration: number) => {
  const minutes = Math.floor(duration / (1000 * 60))
  const hours = Math.floor(duration / (1000 * 60 * 60))
  const days = Math.floor(duration / (1000 * 60 * 60 * 24))

  return {
    hours,
    minutes,
    days,
  }
}

export const timezones = [
  { value: 'Pacific/Midway', label: '(UTC-11:00) Midway Island, Samoa' },
  { value: 'Pacific/Honolulu', label: '(UTC-10:00) Hawaii' },
  { value: 'America/Anchorage', label: '(UTC-09:00) Alaska' },
  { value: 'America/Los_Angeles', label: '(UTC-08:00) Pacific Time (US & Canada)' },
  { value: 'America/Denver', label: '(UTC-07:00) Mountain Time (US & Canada)' },
  { value: 'America/Chicago', label: '(UTC-06:00) Central Time (US & Canada)' },
  { value: 'America/New_York', label: '(UTC-05:00) Eastern Time (US & Canada)' },
  { value: 'America/Caracas', label: '(UTC-04:00) Caracas' },
  { value: 'America/Halifax', label: '(UTC-04:00) Atlantic Time (Canada)' },
  { value: 'America/St_Johns', label: '(UTC-03:30) Newfoundland' },
  { value: 'America/Argentina/Buenos_Aires', label: '(UTC-03:00) Buenos Aires' },
  { value: 'America/Noronha', label: '(UTC-02:00) Mid-Atlantic' },
  { value: 'Atlantic/Azores', label: '(UTC-01:00) Azores' },
  { value: 'Europe/London', label: '(UTC+00:00) London, Dublin, Lisbon, Casablanca' },
  { value: 'Europe/Paris', label: '(UTC+01:00) Paris, Berlin, Madrid, Rome' },
  { value: 'Europe/Athens', label: '(UTC+02:00) Athens, Jerusalem, Cairo' },
  { value: 'Europe/Moscow', label: '(UTC+03:00) Moscow, Baghdad, Nairobi' },
  { value: 'Asia/Tehran', label: '(UTC+03:30) Tehran' },
  { value: 'Asia/Dubai', label: '(UTC+04:00) Abu Dhabi, Muscat' },
  { value: 'Asia/Kabul', label: '(UTC+04:30) Kabul' },
  { value: 'Asia/Karachi', label: '(UTC+05:00) Islamabad, Karachi, Tashkent' },
  { value: 'Asia/Kolkata', label: '(UTC+05:30) Chennai, Kolkata, Mumbai, New Delhi' },
  { value: 'Asia/Kathmandu', label: '(UTC+05:45) Kathmandu' },
  { value: 'Asia/Dhaka', label: '(UTC+06:00) Astana, Dhaka' },
  { value: 'Asia/Rangoon', label: '(UTC+06:30) Yangon (Rangoon)' },
  { value: 'Asia/Bangkok', label: '(UTC+07:00) Bangkok, Hanoi, Jakarta' },
  { value: 'Asia/Hong_Kong', label: '(UTC+08:00) Beijing, Perth, Singapore, Hong Kong' },
  { value: 'Asia/Tokyo', label: '(UTC+09:00) Tokyo, Seoul, Osaka, Sapporo' },
  { value: 'Australia/Adelaide', label: '(UTC+09:30) Adelaide, Darwin' },
  { value: 'Australia/Sydney', label: '(UTC+10:00) Sydney, Brisbane, Melbourne' },
  { value: 'Pacific/Guam', label: '(UTC+10:00) Guam, Port Moresby' },
  { value: 'Pacific/Auckland', label: '(UTC+12:00) Auckland, Wellington, Fiji' },
  { value: 'Pacific/Tongatapu', label: "(UTC+13:00) Nuku'alofa" },
]

const adjustTimezonesForDST = (tzArray: { value: string; label: string }[]) =>
  tzArray.map((tz) => {
    const offset = moment().tz(tz.value).format('Z')
    const originalLabelPart = tz.label.split(')').slice(1).join(')').trim()
    const newLabel = `(UTC${offset}) ${originalLabelPart}`

    return {
      ...tz,
      label: newLabel,
    }
  })

export const adjustedTimezones = adjustTimezonesForDST(timezones)
